import React, { useEffect, useRef, useState } from "react";

declare global {
    interface Window {
        JitsiMeetExternalAPI?: any;
    }
}

interface JitsiMeetingEmbedProps {
    roomName: string;
    displayName: string;
    email?: string;
    subject?: string;
    onReady?: () => void;
    onClose?: () => void;
}

const JITSI_DOMAIN = "meet.jit.si";

const loadJitsiScript = async () => {
    if (window.JitsiMeetExternalAPI) return;

    await new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="https://${JITSI_DOMAIN}/external_api.js"]`) as HTMLScriptElement | null;
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(), { once: true });
            existingScript.addEventListener("error", () => reject(new Error("Failed to load Jitsi script")), { once: true });
            return;
        }

        const script = document.createElement("script");
        script.src = `https://${JITSI_DOMAIN}/external_api.js`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Jitsi script"));
        document.body.appendChild(script);
    });
};

const JitsiMeetingEmbed: React.FC<JitsiMeetingEmbedProps> = ({ roomName, displayName, email, subject, onReady, onClose }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const apiRef = useRef<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;

        const initMeeting = async () => {
            try {
                setLoading(true);
                setError(null);
                await loadJitsiScript();

                if (disposed || !containerRef.current || !window.JitsiMeetExternalAPI) return;

                apiRef.current = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
                    roomName,
                    parentNode: containerRef.current,
                    userInfo: {
                        displayName,
                        email,
                    },
                    configOverwrite: {
                        prejoinPageEnabled: true,
                        startWithAudioMuted: false,
                        startWithVideoMuted: false,
                        enableWelcomePage: false,
                        disableModeratorIndicator: false,
                    },
                    interfaceConfigOverwrite: {
                        SHOW_JITSI_WATERMARK: false,
                        SHOW_BRAND_WATERMARK: false,
                        TOOLBAR_BUTTONS: [
                            "microphone",
                            "camera",
                            "desktop",
                            "fullscreen",
                            "fodeviceselection",
                            "hangup",
                            "chat",
                            "settings",
                            "raisehand",
                            "tileview",
                            "videoquality",
                            "participants-pane",
                            "filmstrip",
                            "invite",
                        ],
                    },
                });

                apiRef.current.addListener("videoConferenceJoined", () => {
                    if (subject) {
                        apiRef.current.executeCommand("subject", subject);
                    }
                    setLoading(false);
                    onReady?.();
                });

                apiRef.current.addListener("readyToClose", () => {
                    onClose?.();
                });
            } catch (meetingError: any) {
                console.error("Jitsi initialization error:", meetingError);
                setError(meetingError?.message || "Failed to initialize meeting");
                setLoading(false);
            }
        };

        initMeeting();

        return () => {
            disposed = true;
            if (apiRef.current) {
                apiRef.current.dispose();
                apiRef.current = null;
            }
        };
    }, [roomName, displayName, email, subject, onReady, onClose]);

    return (
        <div className="sc-panel" style={{ overflow: "hidden" }}>
            <div className="sc-panel-body" style={{ padding: 0, position: "relative", minHeight: 680 }}>
                {loading && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.92)", zIndex: 2 }}>
                        <div className="sc-spinner"></div>
                    </div>
                )}
                {error ? (
                    <div className="sc-empty">
                        <i className="bx bx-error-circle"></i>
                        <h3>Meeting unavailable</h3>
                        <p>{error}</p>
                    </div>
                ) : (
                    <div ref={containerRef} style={{ minHeight: 680, width: "100%" }} />
                )}
            </div>
        </div>
    );
};

export default JitsiMeetingEmbed;
